import { createStore, createHook, createContainer, createActionsHook } from 'react-sweet-state';
import type { Action } from 'react-sweet-state';

import type { Ari } from '@atlassian/ari';

import {
	confluenceLocalStorageInstance as localStorage,
	keys as localStorageKeys,
} from '@confluence/storage-manager';

import { getBriefing } from '../api/speechAPI';
import type { AudioRequest } from '../types/audioRequestType';
import type { ActiveAudioContent } from '../types/activeAudioContent';
import { getConfluenceContentAri } from '../utils/getConfluenceContentAri';

export type AudioBriefingState = {
	activeContent?: ActiveAudioContent;
	additionalResources: Ari[];
	cloudId: string;
	activationId: string;
	locale: string;
	isLoading: boolean;
	isPlaying: boolean;
	isClosed: boolean;
	isEnded: boolean;
	hasFetched: boolean;
	playbackSpeed: number;
	error?: Error;
	stream?: ReadableStream<Uint8Array>;
};

export type AudioBriefingInitialStateInput = Required<
	Pick<AudioBriefingState, 'activeContent' | 'cloudId' | 'activationId' | 'locale'>
>;

const initialAudioBriefingState: AudioBriefingState = {
	activeContent: undefined,
	additionalResources: [],
	cloudId: '',
	activationId: '',
	locale: 'en-US',
	isLoading: false,
	isPlaying: false,
	isClosed: false,
	isEnded: false,
	hasFetched: false,
	playbackSpeed: 1,
	stream: undefined,
	error: undefined,
};

type AudioBriefingContainerProps = {
	initialAudioBriefingState: AudioBriefingInitialStateInput;
};

type AudioBriefingActions = {
	initialize: (
		initialState: AudioBriefingInitialStateInput,
	) => Action<AudioBriefingState, void, void>;
	reset: () => Action<AudioBriefingState, void, void>;
	fetchBriefing: () => Action<AudioBriefingState, void, void>;
	onLoadStart: () => Action<AudioBriefingState, void, void>;
	onLoadEnd: () => Action<AudioBriefingState, void, void>;
	pauseContent: () => Action<AudioBriefingState, void, void>;
	resumeContent: () => Action<AudioBriefingState, void, void>;
	setContentPlaybackSpeed: (speed: number) => Action<AudioBriefingState, void, void>;
	stopContent: () => Action<AudioBriefingState, void, void>;
	endContent: () => Action<AudioBriefingState, void, void>;
	setContentError: (error: Error) => Action<AudioBriefingState, void, void>;
};

export const audioBriefingActions: AudioBriefingActions = {
	initialize:
		(initialState: AudioBriefingInitialStateInput) =>
		async ({ setState }) => {
			const emptyAudioBriefingState: AudioBriefingState = {
				stream: undefined,
				activeContent: undefined,
				additionalResources: [],
				locale: 'en-US',
				cloudId: '',
				activationId: '',
				isLoading: false,
				isPlaying: false,
				isClosed: false,
				isEnded: false,
				hasFetched: false,
				error: undefined,
				playbackSpeed: Number(localStorage.getItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED)) || 1,
			};
			setState(Object.assign(emptyAudioBriefingState, initialState));
		},
	reset:
		() =>
		({ setState }) => {
			setState(initialAudioBriefingState);
		},
	fetchBriefing:
		() =>
		async ({ getState, setState, dispatch }) => {
			dispatch(audioBriefingActions.onLoadStart());

			const { activeContent, cloudId, activationId, locale } = getState();

			if (!activeContent) {
				throw new Error('Briefing container needs to be initialized');
			}

			const pageAri = getConfluenceContentAri(
				cloudId,
				activeContent.id,
				activeContent.contentType ?? 'page',
			);

			const audioRequest: AudioRequest = {
				pageAri,
				activationId,
				locale,
			};

			try {
				const stream = await getBriefing(audioRequest);

				if (getState().isClosed) {
					return;
				}

				setState({
					isPlaying: false,
					isEnded: false,
					stream,
				});
			} catch (e) {
				setState({
					error: e,
					activeContent: undefined,
					stream: undefined,
				});
			} finally {
				dispatch(audioBriefingActions.onLoadEnd());
			}
		},
	onLoadStart:
		() =>
		({ setState }) => {
			setState({
				isLoading: true,
				hasFetched: true,
			});
		},
	onLoadEnd:
		() =>
		({ setState }) => {
			setState({ isLoading: false });
		},
	pauseContent:
		() =>
		({ setState }) => {
			setState({
				isPlaying: false,
			});
		},
	resumeContent:
		() =>
		({ setState, getState }) => {
			const { stream } = getState();
			if (!stream) {
				throw new Error('Must fetch before playing');
			}
			setState({
				isPlaying: true,
				isEnded: false,
			});
		},
	setContentPlaybackSpeed:
		(speed: number) =>
		({ setState }) => {
			localStorage.setItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED, speed);
			setState({ playbackSpeed: speed });
		},
	stopContent:
		() =>
		async ({ dispatch, setState }) => {
			dispatch(audioBriefingActions.reset());
			setState({ isClosed: true });
		},
	endContent:
		() =>
		({ setState }) => {
			setState({
				isEnded: true,
				isPlaying: false,
			});
		},
	setContentError:
		(error: Error) =>
		({ setState }) => {
			setState({
				error,
			});
		},
};

const Store = createStore<AudioBriefingState, AudioBriefingActions>({
	initialState: {
		activeContent: undefined,
		additionalResources: [],
		cloudId: '',
		activationId: '',
		locale: 'en-US',
		isLoading: false,
		isPlaying: false,
		isClosed: false,
		isEnded: false,
		hasFetched: false,
		playbackSpeed: 1,
		error: undefined,
	},
	actions: audioBriefingActions,
	name: 'listenerState',
});

export const AudioBriefingContainer = createContainer<
	AudioBriefingState,
	AudioBriefingActions,
	AudioBriefingContainerProps
>(Store, {
	onInit:
		() =>
		async ({ dispatch }, { initialAudioBriefingState: initialAudioPlaybackState }) => {
			void dispatch(audioBriefingActions.initialize(initialAudioPlaybackState));
		},
});

export const useAudioBriefing = createHook<AudioBriefingState, AudioBriefingActions>(Store);
export const useAudioBriefingActions = createActionsHook(Store);
